import React, { lazy } from 'react';
import { useRoutes } from 'react-router';
import { Navigate, type RouteObject } from 'react-router';
import { AuthGuard } from 'routes/AuthGuard';
import { Login } from 'screens/Login';
import { Profile } from 'screens/Profile';
import { SignUp } from 'screens/SignUp';
import { NotFound } from 'screens/NotFound';
import { PendingApproval } from 'screens/PendingApproval';
import { Settings } from 'screens/Settings';
import { FeatureFlagKeys } from 'features/DeveloperTools/config/constants';
import { Resources } from 'screens/Resources';
import { CareProgramsListPage } from 'components/CareProgramsListPage/CareProgramsListPage';
import { HealthTopicsBrowsePage } from 'screens/HealthTopicsBrowsePage';
import { UnauthenticatedAssessments } from 'screens/UnauthenticatedAssessments';
import { UnauthenticatedAssessmentsVerify } from 'screens/UnauthenticatedAssessmentsVerify';
import { useFeatureFlags } from 'features/DeveloperTools/hooks/useFeatureFlags';
import { useResourceView } from 'features/resources/hooks/useResourceView';
import { Enrollment } from 'screens/Enrollment';
import { LibraryPage } from 'components/Resources/LibraryPage';
import { LibrarySearchPage } from 'components/Resources/LibrarySearchPage';
import { CareProgramSearchPage } from 'components/Resources/CareProgramSearchPage';
import { Progress } from 'screens/Progress';
import { BenefitsPage } from 'components/Resources/BenefitsPage';
import { WeightDetails } from 'screens/WeightDetails';
import { StepCountDetailPage } from 'components/ProgressTab/StepCountDetail/StepCountDetailPage';
import { BloodPressureDetails } from 'screens/BloodPressureDetails';
import { WidgetDetail } from 'screens/WidgetDetail';
import { RouteFallback } from './RouteFallback';
import { GuestGuard } from './GuestGuard';
import { CobrandGuard } from './CobrandGuard';
import { HostCobrandGuard } from './HostCobrandGuard';
import { UnauthenticatedAssessmentsWrapper } from './UnauthenticatedAssessmentsWrapper';
import { AppLinkGuard } from './AppLinkGuard';

const LazyHome = RouteFallback(
  lazy(() => import('screens/Home').then((module) => ({ default: module.Home }))),
);

const ResourceTopicsRoute = () =>
  useResourceView() ? <CareProgramsListPage /> : <HealthTopicsBrowsePage />;

// Gates resource sub-routes on the feature flag from inside route context, where the cobrand resolves correctly.
const ResourceFeatureGuard = ({ children }: { children: React.ReactElement }) => {
  const { isEnabled } = useFeatureFlags();
  return isEnabled(FeatureFlagKeys.RESOURCE_FEATURE) ? children : <NotFound />;
};

const CareProgramSearchFeatureGuard = ({ children }: { children: React.ReactElement }) => {
  const { isEnabled } = useFeatureFlags();
  return isEnabled(FeatureFlagKeys.SEARCH_CARE_PROGRAMS) ? children : <NotFound />;
};

const ProfileFeatureGuard = ({ children }: { children: React.ReactElement }) => {
  const { isEnabled } = useFeatureFlags();
  return isEnabled(FeatureFlagKeys.PROFILE_FEATURE) ? children : <NotFound />;
};

const BenefitsFeatureGuard = ({ children }: { children: React.ReactElement }) => {
  const { isEnabled } = useFeatureFlags();
  return isEnabled(FeatureFlagKeys.BENEFITS_FEATURE) ? children : <NotFound />;
};

const BaseRoutes: RouteObject[] = [
  {
    element: <GuestGuard />,
    children: [
      { path: 'login', element: <Login /> },
      { path: 'signup', element: <SignUp /> },
    ],
  },
  // App link routes (outside AuthGuard so AppLinkGuard can handle auth + returnUrl)
  {
    path: 'enrollment',
    element: (
      <AppLinkGuard
        config={{
          requiresAuth: true,
          requiredParams: ['enrollmentId'],
        }}
      >
        <Enrollment />
      </AppLinkGuard>
    ),
  },
  {
    element: <AuthGuard />,
    children: [
      { index: true, element: <Navigate to="home" replace /> },
      { path: 'home', element: <LazyHome /> },
      { path: 'progress', element: <Progress /> },
      { path: 'progress/weight', element: <WeightDetails /> },
      { path: 'progress/steps', element: <StepCountDetailPage /> },
      { path: 'progress/blood-pressure', element: <BloodPressureDetails /> },
      { path: 'progress/:widget', element: <WidgetDetail /> },
      { path: 'resources', element: <Resources /> },
      {
        path: 'profile',
        element: (
          <ProfileFeatureGuard>
            <Profile />
          </ProfileFeatureGuard>
        ),
      },
      { path: 'settings', element: <Settings /> },
      { path: 'pending-approval', element: <PendingApproval /> },
    ],
  },
  { path: '*', element: <NotFound /> },
];
export const routes: RouteObject[] = [
  // Path-based routing: /member-web/:cobrand/home
  {
    path: '/:cobrand',
    element: <CobrandGuard />,
    children: BaseRoutes,
  },
  // Hostname-based routing: caremanagement.carefirst.com/home
  {
    path: '',
    element: <HostCobrandGuard />,
    children: BaseRoutes,
  },
];

const UnauthenticatedAssessmentsRoutes: RouteObject = {
  path: 'assessments',
  element: <UnauthenticatedAssessmentsWrapper />,
  children: [
    { index: true, element: <UnauthenticatedAssessments /> },
    { path: 'verify', element: <UnauthenticatedAssessmentsVerify /> },
  ],
};

export const WellframeMemberWebRoutes = () => {
  const { isEnabled } = useFeatureFlags();
  const isUnauthenticatedAssessmentsEnabled = isEnabled(
    FeatureFlagKeys.UNAUTHENTICATED_ASSESSMENTS,
  );

  // TODO: Remove this logic once Resources feature is live and nested routes are merged into BaseRoutes
  const baseRoutes: RouteObject[] = BaseRoutes.map((route) => {
    // Return routes without children as-is
    if (!route?.children) {
      return route;
    }
    // Register resource sub-routes unconditionally; the ResourceFeatureGuard gates them in route context.
    const updatedChildren: RouteObject[] = route.children.map((child) => {
      if (child?.path === 'resources') {
        return {
          path: 'resources',
          children: [
            { index: true, element: <Resources /> },
            {
              path: 'topics',
              element: (
                <ResourceFeatureGuard>
                  <ResourceTopicsRoute />
                </ResourceFeatureGuard>
              ),
            },
            {
              path: 'topics/search',
              element: (
                <ResourceFeatureGuard>
                  <CareProgramSearchFeatureGuard>
                    <CareProgramSearchPage />
                  </CareProgramSearchFeatureGuard>
                </ResourceFeatureGuard>
              ),
            },
            {
              path: 'library',
              element: (
                <ResourceFeatureGuard>
                  <LibraryPage />
                </ResourceFeatureGuard>
              ),
            },
            {
              path: 'library/search',
              element: (
                <ResourceFeatureGuard>
                  <LibrarySearchPage />
                </ResourceFeatureGuard>
              ),
            },
            {
              path: 'benefits',
              element: (
                <ResourceFeatureGuard>
                  <BenefitsFeatureGuard>
                    <BenefitsPage />
                  </BenefitsFeatureGuard>
                </ResourceFeatureGuard>
              ),
            },
          ],
        };
      }
      return child;
    });

    // Return new route with updated children (explicitly construct to avoid type issues)
    return {
      path: route.path,
      element: route.element,
      children: updatedChildren,
    };
  });

  // Build BaseRoutes with optional UnauthenticatedAssessments
  const baseRoutesWithAssessments = isUnauthenticatedAssessmentsEnabled
    ? [...baseRoutes, UnauthenticatedAssessmentsRoutes]
    : baseRoutes;

  // Apply cobrand guards to the complete route tree
  const routesWithCobrandGuards: RouteObject[] = [
    // Path-based routing: /member-web/:cobrand/home
    {
      path: '/:cobrand',
      element: <CobrandGuard />,
      children: baseRoutesWithAssessments,
    },
    // Hostname-based routing: caremanagement.carefirst.com/home
    {
      path: '',
      element: <HostCobrandGuard />,
      children: baseRoutesWithAssessments,
    },
  ];

  const router = useRoutes(routesWithCobrandGuards);
  return router;
};
